import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BodyText, SideDrawer, withStyles } from 'vopay';
import type { AccountUserRole, AccountUserRoleUser } from 'vopay';
import PermissionsSearchField from '../UserPermissions/PermissionsSearchField';
import styles from './RoleUsersViewDrawer.module.scss';

const cn = withStyles(styles);

type RoleUsersViewDrawerProps = {
  isOpen: boolean;
  role: AccountUserRole | null;
  users: AccountUserRoleUser[];
  fetching: boolean;
  onClose: () => void;
};

/**
 * Stacked sub-drawer listing the users assigned to the selected role.
 * Data is fetched by the parent component (UsersList) so the count shown
 * in Role Details and the list shown here come from the same response.
 */
const RoleUsersViewDrawer: FC<RoleUsersViewDrawerProps> = ({
  isOpen,
  role,
  users,
  fetching,
  onClose,
}) => {
  const { t } = useTranslation('translations', { keyPrefix: 'Users.UserRoles.usersViewDrawer' });

  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (isOpen) setSearchTerm('');
  }, [isOpen]);

  const filteredUsers = useMemo(() => {
    const needle = searchTerm.trim().toLowerCase();
    if (!needle) return users;
    return users.filter((u) => u.Username.toLowerCase().includes(needle));
  }, [users, searchTerm]);

  if (!role) return null;

  return (
    <SideDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={t('title', { defaultValue: 'Users' })}
      headerFullWidthContent={
        <PermissionsSearchField
          name="roleUsersSearch"
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder={t('searchPlaceholder', { defaultValue: 'Search Users' })}
        />
      }
    >
      <div className={cn('body')}>
        {fetching && (
          <BodyText weight="md">
            {t('loading', { defaultValue: 'Loading users…' })}
          </BodyText>
        )}

        {!fetching && filteredUsers.length === 0 && (
          <BodyText weight="md" className={cn('emptyState')}>
            {searchTerm
              ? t('noMatches', { defaultValue: 'No users match your search.' })
              : t('noUsers', { defaultValue: 'No users assigned to this role.' })}
          </BodyText>
        )}

        {!fetching && filteredUsers.length > 0 && (
          <ul className={cn('userList')}>
            {filteredUsers.map((u) => (
              <li key={u.UserID} className={cn('userItem')}>
                {u.Username}
              </li>
            ))}
          </ul>
        )}
      </div>
    </SideDrawer>
  );
};

export default RoleUsersViewDrawer;
